import getConfig from 'next/config'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import SearchButton, { SearchButtonContainer } from './SearchButton'
import { breakpoint, size } from './styles'
import Topbar, { Container as TopbarContainer } from './Topbar'

const Navigation = ({
  navigation_menu: items,
  searchOpen,
  setSearchOpen,
  menuOpen,
  setMenuOpen,
  megamenu,
  setMegamenu
}: any) => {
  const router = useRouter()
  const { publicRuntimeConfig } = getConfig()

  // router.asPath includes the locale prefix (e.g. /en-CA/recipes/...) which
  // must be stripped before passing to <Link locale=...> to avoid double-prefixing.
  const localelessPath = (() => {
    const prefix = `/${router.locale}`
    return router.asPath.startsWith(prefix) ? router.asPath.slice(prefix.length) || '/' : router.asPath
  })()

  const handleNavButtonClick = (e: any) => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
    e.preventDefault()
  }

  const mobileButtons = items?.filter((link: any) => link.button && link.button_out_mobile)

  return (
    <Container anchorRight={publicRuntimeConfig?.navigationAnchorRight}>
      {mobileButtons && (
        <MobileButtonsContainer>
          {mobileButtons.map((link: any) => (
            <MobileButton key={link.label} button_color={link.button_color} text_color={link.text_color}>
              {link.button_icon && <img src={`/images/${link.button_icon}.svg`} width={20} height={20} />}
              <Link href={link.page_reference[0].url} passHref>
                <MenuLink star={link.page_reference[0].url === '/offers'}>{link.label}</MenuLink>
              </Link>
            </MobileButton>
          ))}
        </MobileButtonsContainer>
      )}
      <MenuItems open={menuOpen}>
        {items?.map((link: any) => (
          <MenuItem
            key={link.label}
            anchorRight={publicRuntimeConfig?.navigationAnchorRight}
            selected={
              megamenu === link._metadata.uid ||
              (link.page_reference.length > 0 && router.asPath.indexOf(link.page_reference[0].url) > -1)
            }
            className={link.button && 'menu-item-button'}
            button={link.button}
            button_color={link.button_color}
            hide_in_mobile={link.button_out_mobile}
            text_color={link.text_color}
            onClick={() => (link.page_reference.length > 0 ? setMenuOpen(false) : null)}>
            {link.button && link.button_icon && <img src={`/images/${link.button_icon}.svg`} width={20} height={20} />}

            {link.page_reference.length > 0 ? (
              <Link href={link.page_reference[0].url} passHref>
                <MenuLink star={link.page_reference[0].url === '/offers'}>{link.label}</MenuLink>
              </Link>
            ) : (
              <Megamenu
                className={megamenu === link._metadata.uid && 'megamenu-is-open'}
                onClick={() => setMegamenu(megamenu === link._metadata.uid ? null : link._metadata.uid)}>
                {link.label}
              </Megamenu>
            )}
            {megamenu === link._metadata.uid && (
              <MenuContent horizontal={link.items.find((item: any) => item.subitems?.length > 0)}>
                {link.items?.map((item: any) => (
                  <li key={item._metadata.uid}>
                    {item.page_reference[0] ? (
                      <Link href={item.page_reference[0].url}>
                        <a
                          onClick={() => {
                            setMegamenu(null)
                            setMenuOpen(false)
                          }}>
                          {item.label}
                        </a>
                      </Link>
                    ) : item.link?.href ? (
                      <a
                        href={item.link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setMegamenu(null)
                          setMenuOpen(false)
                        }}>
                        {item.label}
                      </a>
                    ) : (
                      item.label && <Label>{item.label}</Label>
                    )}
                    {item.image && (
                      <Image>
                        <img src={`${item.image.url}?auto=webp&width=380`} alt={item.image.alt || ''} aria-hidden />
                      </Image>
                    )}
                    {item.subitems && item.subitems?.length > 0 && (
                      <ul>
                        {item.subitems.map((subitem: any) => (
                          <li key={subitem._metadata.uid}>
                            <Link href={subitem.link.href}>
                              <a
                                onClick={() => {
                                  setMegamenu(null)
                                  setMenuOpen(false)
                                }}>
                                {subitem.link.title}
                              </a>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </MenuContent>
            )}
          </MenuItem>
        ))}
        {!publicRuntimeConfig?.disableLanguageSelector && !publicRuntimeConfig?.countryBasedLanguageSelector && (
          <LanguageContainer>
            <LanguageSelector selected={router.locale !== 'fr'} style={{ marginRight: 7 }}>
              <Link href={localelessPath} locale={'en-US'} prefetch={false}>
                EN
              </Link>
            </LanguageSelector>
            <MenuItemBase> / </MenuItemBase>
            <LanguageSelector selected={router.locale === 'fr'} style={{ marginLeft: 7 }}>
              <Link href={localelessPath} locale={'fr'} prefetch={false}>
                FR
              </Link>
            </LanguageSelector>
          </LanguageContainer>
        )}
        {!publicRuntimeConfig?.disableLanguageSelector && publicRuntimeConfig?.countryBasedLanguageSelector && (
          <LanguageContainer>
            <Flag>
              {' '}
              <img src="/images/us-flag.svg" width="24" />{' '}
            </Flag>
            <LanguageSelector selected={router.locale === 'en-US'} style={{ marginRight: 7, marginLeft: 7 }}>
              <Link href={router.locale !== 'en-US' ? '/' : localelessPath} locale={'en-US'} prefetch={false}>
                EN
              </Link>
            </LanguageSelector>
            <Flag>
              {' '}
              <img src="/images/ca-flag.svg" width="24" />{' '}
            </Flag>
            <LanguageSelector selected={router.locale === 'en-CA'} style={{ marginRight: 7, marginLeft: 7 }}>
              <Link href={router.locale === 'en-US' ? '/' : localelessPath} locale={'en-CA'} prefetch={false}>
                EN
              </Link>
            </LanguageSelector>
            <MenuItemBase> / </MenuItemBase>
            <LanguageSelector selected={router.locale === 'fr'} style={{ marginLeft: 7 }}>
              <Link href={router.locale === 'en-US' ? '/' : localelessPath} locale={'fr'} prefetch={false}>
                FR
              </Link>
            </LanguageSelector>
          </LanguageContainer>
        )}
        {publicRuntimeConfig?.dropdownLanguageSelector && <Topbar {...{ searchOpen, setSearchOpen, setMenuOpen }} />}
        <MenuSearchButton>
          <SearchButton {...{ searchOpen, setSearchOpen }} mobileOnly={publicRuntimeConfig?.dropdownLanguageSelector} />
        </MenuSearchButton>
      </MenuItems>
      <SearchButton {...{ searchOpen, setSearchOpen }} mobileOnly={publicRuntimeConfig?.dropdownLanguageSelector} />
      <NavButton open={searchOpen} style={{ marginRight: 0 }} onClick={handleNavButtonClick}>
        <img src={`/images/${menuOpen ? 'close' : 'menu'}.svg`} alt="Menu" width="24" height="24" />
      </NavButton>
    </Container>
  )
}

export default Navigation

export const Megamenu = styled.span<any>`
  display: inline-block;
  padding-right: 0;
  margin-left: 2rem;

  &:after {
    content: url(/images/down.svg);
    margin-left: 0.09375rem;
  }
`

const Image = styled.div<any>`
  margin: 0 1rem 1.5rem 1rem;
  display: none;
  @media ${breakpoint.desktop} {
    display: inherit;
  }
  img {
    width: 190px;
  }
`

export const Container = styled.nav<any>`
  color: var(--text);
  display: flex;
  align-items: flex-end;
  ${(props) =>
    props.anchorRight &&
    css`
      justify-content: flex-end;
    `};
  a,
  ${Megamenu} {
    color: var(--text);
    text-decoration: none;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 1.125rem;
    display: block;
    cursor: pointer;
  }
  ${(props) =>
    props.anchorRight &&
    css`
      &:last-child {
        margin-right: 0;
      }
    `};

  @media print {
    display: none;
  }
`
const rotate = keyframes`
  from {
    transform: translateX(-50%);
    width: 50%;
  }

  to {
    transform: translateX(0);
    width: 100%;
  }
`

const MenuLink = styled.a<any>`
  ${(props) =>
    props.star &&
    css`
      padding-right: 30px;
      background: transparent url('/images/star.svg') no-repeat scroll right center;
      background-size: 24px;
    `}
`

const Label = styled.span<any>`
  padding: 1rem;
  display: block;
`

export const MenuItems = styled.div<any>`
  display: ${(props) => (props.open ? 'flex' : 'none')};
  position: absolute;
  border-top: 2px #c4c4c4 solid;
  top: 62px;
  right: 0;
  background: var(--background);
  flex-direction: column;
  width: 100%;
  @media ${breakpoint.tablet} {
    max-width: ${size.mobile};
  }
  @media screen and ${breakpoint.desktop} {
    border-top: 0;
    display: flex;
    position: static;
    background: transparent;
    flex-direction: row;
    width: inherit;
    max-width: inherit;
  }
  ${(props) =>
    props.open &&
    css`
      &:before {
        position: fixed;
        content: '';
        xtop: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: var(--background);
        z-index: -1;
        @media ${breakpoint.tablet} {
          width: 375px;
        }
        @media ${breakpoint.desktop} {
          content: inherit;
        }
      }
    `};

  ${TopbarContainer} {
    display: flex;
    @media ${breakpoint.desktop} {
      display: none;
    }
  }

  ${SearchButtonContainer} {
    display: none;
  }
`

const MenuItemBase = styled.div<any>`
  font-weight: 700;
  font-size: 1.125rem;
  margin: 0;
  background-color: rgba(0, 0, 0, 0);
  color: var(--nav-text, var(--text));
`

export const Flag = styled(MenuItemBase)`
  margin-left: 17px;
`

const MenuContent = styled.ul<any>`
  ${(props) =>
    props.horizontal &&
    css`
      &&& {
        & > li {
          background: #ededed;
          & > span {
            padding-left: 1.5rem;
          }
          li {
            &:last-child {
              margin-bottom: 0;
            }
          }
        }

        @media ${breakpoint.desktop} {
          display: flex;
          flex-direction: row;
          width: auto;
          padding: 0.5rem 0.5rem 1rem;
          margin-left: -1rem;
          & > li {
            min-width: 180px;
            background: #fff;
            a {
              padding: 0.5rem 1rem;
            }

            & > span {
              padding-left: 1rem;
            }
          }
        }
      }
    `}
`

const MobileButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;

  @media ${breakpoint.desktop} {
    display: none;
  }
`

const MobileButton = styled.div<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem 1rem;
  border-radius: 1rem;

  ${(props) =>
    props.button_color &&
    props.text_color &&
    css`
      background-color: var(--${props.button_color});
      a {
        font-weight: 100;
        color: var(--${props.text_color});
      }
    `}

  ${MenuLink} {
    font-weight: 700;
  }

  img {
    margin-right: 0.5rem;
  }

  a {
    text-decoration: none;
  }
`

export const MenuSearchButton = styled.div<any>`
  display: none;
`

export const MenuItem = styled(MenuItemBase)`
  position: relative;
  padding: 1rem 1.5rem;
  border-bottom: 1px #666 solid;
  color: var(--text);
  @media ${breakpoint.desktop} {
    margin: 0 27px;
    padding: 0;
    border-bottom: 0;
    position: relative;

    ${(props) =>
      props.anchorRight &&
      css`
        &:last-child {
          margin-right: 0;
        }
      `};

    &:hover:after {
      content: '';
      background: var(--text);
      width: 100%;
      height: 2.5px;
      position: absolute;
      bottom: -8px;
      left: 0;
      transition: all 250ms ease;
      transform: translateX(0);
      animation: ${rotate} 250ms ease 1;
    }

    ${(props) =>
      props.open &&
      css`
        display: block;
      `}

    ${(props) =>
      props.button &&
      props.button_color &&
      css`
        background-color: var(--${props.button_color});
        text-align: center;
        padding: 0.5rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 160px;
        a {
          font-weight: 100;
          color: var(--${props.text_color}) !important;
        }
      `}
  }

  @media ${breakpoint.desktop_down} {
    ${(props) =>
      props.hide_in_mobile &&
      css`
        display: none;
      `}
  }

  ul,
  li {
    list-style: none;
    padding: 0;
  }

  & > ul {
    background: #fff;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    margin-bottom: 0;
    @media ${breakpoint.desktop} {
      margin-left: 0;
      margin-right: 0;
      position: absolute;
      border: 1.5px #000 solid;
      width: 250px;
    }

    li {
      &:last-child {
        margin-bottom: -1rem;
        @media ${breakpoint.desktop} {
          margin-bottom: 0;
        }
      }
      background: #fff;
    }

    a {
      text-transform: none;
      font-weight: normal;
      font-size: 14px;
      padding: 1rem 1.5rem;
      border-top: 1px #666 solid;
      @media ${breakpoint.desktop} {
        font-size: 16px;
        padding: 1rem 1rem;
        border-top: 0;
      }
      &:hover {
        background: var(--text);
        color: var(--background);
      }
    }
  }

  ${(props) =>
    props.selected &&
    css`
      @media ${breakpoint.desktop} {
        &:after {
          content: '';
          background: var(--text);
          width: 100%;
          height: 2.5px;
          position: absolute;
          bottom: -8px;
          left: 0px;
          transition: all 250ms ease;
          transform: translateX(0);
          animation: ${rotate} 250ms ease 1;
        }
      }

      @media ${breakpoint.desktop_down} {
        ${MenuLink} {
          &.nav-menu-link:after {
            content: url(/images/down.svg);
          }
        }
      }
    `}
  img {
    position: relative;
    top: 7px;
  }
  a,
  span {
    color: var(--nav-text, var(--text));
  }
  ${(props) =>
    props.selected &&
    css`
      background-color: var(--nav-bg-selected);
      a,
      span {
        color: var(--nav-text-selected);
      }
    `}
`
export const LanguageContainer = styled.div<any>`
  display: flex;
  padding: 1rem 1.5rem;
  border-bottom: 1px #666 solid;
  --nav-text-selected: var(--text);
  @media ${breakpoint.desktop} {
    padding: 0;
    border-bottom: 0;
  }
`

export const LanguageSelector = styled(MenuItem)`
  border-bottom: 0;
  padding: 0;
  ${(props) =>
    props.selected &&
    css`
      &:after {
        content: '';
        background: var(--nav-text-selected);
        width: 100%;
        height: 2.5px;
        position: absolute;
        bottom: -4px;
        left: 0px;
        transition: all 250ms ease;
        transform: translateX(0);
        animation: ${rotate} 250ms ease 1;
        @media ${breakpoint.desktop} {
          bottom: -8px;
        }
      }
    `}
`

export const NavButton = styled(MenuItem)<any>`
  border-bottom: 0;
  padding: 0;
  margin: 0 0 0 2rem;
  @media ${breakpoint.desktop} {
    margin: 0;
    display: none;
  }
`
